import { AnimatePresence, motion } from 'framer-motion';

import { KimUploadedFile } from '../hooks/use-kim-file-upload';
import { formatFileSize } from '@/new/components/file-uploader/utils';
import { Icon } from '@/new/components/icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { getFileIconClass } from '@/new/lib/file-type-display';
import { cn } from '@/new/lib/utils';

interface KimUploadedFilesListProps {
  uploadedFiles: KimUploadedFile[];
  isUploading?: boolean;
  onFileRemove?: (fileId: string) => void;
  onFileClick?: (fileId: string) => void;
  className?: string;
}

const ICON_SIZE_CLASS = 'size-5.75';

export function KimUploadedFilesList({
  uploadedFiles,
  isUploading = false,
  onFileRemove,
  onFileClick,
  className,
}: KimUploadedFilesListProps) {
  const shouldShow = uploadedFiles.length > 0 || isUploading;

  return (
    <AnimatePresence initial={false}>
      {shouldShow && (
        <motion.div
          key="file-list"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.1 }}
          className="overflow-x-hidden"
        >
          <div className="flex w-full gap-2 overflow-x-auto pt-2 [scrollbar-width:none]">
            {uploadedFiles.map((file) => (
              <Tooltip key={file.id}>
                <TooltipTrigger type="button" onClick={onFileClick ? () => onFileClick(file.id) : undefined}>
                  <div
                    className={cn(
                      'group relative flex h-10.5 w-42 shrink-0 items-center gap-1 rounded-md border border-gray-200 bg-gray-50/80 px-1.75 text-xs text-gray-600',
                      onFileClick && 'cursor-pointer transition-colors hover:border-gray-300 hover:bg-white',
                      className,
                    )}
                  >
                    <Icon className={cn(getFileIconClass(file.extension), ICON_SIZE_CLASS)} />

                    <div className="mt-0.5 flex flex-col leading-tight">
                      <div className="flex items-center gap-1">
                        <span className="line-clamp-1 text-left break-all">{file.name}</span>
                        {onFileRemove && (
                          <button
                            type="button"
                            onClick={() => onFileRemove(file.id)}
                            className="absolute -top-1.5 -right-1.75 z-10 rounded-full border border-gray-300 bg-gray-50 p-0.5 text-gray-500 opacity-0 duration-150 group-hover:opacity-100 hover:text-gray-600"
                          >
                            <Icon className="lucide--x size-3" />
                          </button>
                        )}
                      </div>

                      <div className="flex items-center gap-1 text-[11px] font-light whitespace-nowrap text-gray-400">
                        <span className="uppercase">{file.extension}</span>
                        <span className="text-gray-300">•</span>
                        <span>{formatFileSize(file.size)}</span>
                      </div>
                    </div>
                  </div>
                </TooltipTrigger>

                <TooltipContent sideOffset={4}>{file.name}</TooltipContent>
              </Tooltip>
            ))}

            {isUploading && (
              <div className="flex h-10.5 max-w-56 items-center gap-2 rounded-md border border-dashed border-gray-200 bg-gray-50/60 px-2 text-xs text-gray-400">
                <div className="flex w-5 items-center justify-center">
                  <Icon className="lucide--loader animate-spin text-base ease-in-out" />
                </div>

                <div className="flex flex-1 flex-col gap-1">
                  <div className="h-3 w-28 animate-pulse rounded bg-gray-200/60" />
                  <div className="flex items-center gap-1.5">
                    <div className="h-3 w-6 animate-pulse rounded bg-gray-200/60" />
                    <div className="h-3 w-10 animate-pulse rounded bg-gray-200/60" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
